import type { ApiComponents } from '@aztec-realms/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { browserApiClient, requiredApiData } from './browser-api';
import { verticalSliceQueryKey } from './use-vertical-slice';
import {
  tribeAssistanceQueryKey,
  tribeExpeditionQueryKey,
  tribeSocialQueryKey,
} from './tribe-query-keys';

export { tribeExpeditionQueryKey } from './tribe-query-keys';

export type CivicExpedition = ApiComponents['schemas']['CivicExpeditionResponse'];
export type CivicExpeditionChoice = ApiComponents['schemas']['CivicExpeditionChoiceResponse'];
export type CivicNextGoal = ApiComponents['schemas']['CivicNextGoalResponse'];
export type ExpeditionWorkstream = 'wayfinder' | 'scribe' | 'builder';
export type ExpeditionCommand =
  | { type: 'start'; commandId: string; templateCode?: string; branchCode?: string }
  | { type: 'join'; commandId: string; expeditionId: string }
  | {
      type: 'contribute';
      commandId: string;
      expeditionId: string;
      workstream: ExpeditionWorkstream;
    };

export async function loadTribeExpeditions(tribeId: string, signal?: AbortSignal) {
  const response = await browserApiClient().GET('/api/v1/tribes/{tribe_id}/expeditions', {
    params: { path: { tribe_id: tribeId } },
    signal,
  });
  return requiredApiData(response.data, response.error, 'Expeditions could not be loaded.');
}

export async function submitExpeditionCommand(tribeId: string, command: ExpeditionCommand) {
  const client = browserApiClient({ mutation: true });
  const header = { 'Idempotency-Key': command.commandId };
  const response =
    command.type === 'start'
      ? await client.POST('/api/v1/tribes/{tribe_id}/expeditions/start', {
          params: { path: { tribe_id: tribeId }, header },
          body: {
            ...(command.templateCode ? { template_code: command.templateCode } : {}),
            ...(command.branchCode ? { branch_code: command.branchCode } : {}),
          },
        })
      : command.type === 'join'
        ? await client.POST('/api/v1/tribes/{tribe_id}/expeditions/{expedition_id}/join', {
            params: { path: { tribe_id: tribeId, expedition_id: command.expeditionId }, header },
            body: {},
          })
        : await client.POST('/api/v1/tribes/{tribe_id}/expeditions/{expedition_id}/contribute', {
            params: { path: { tribe_id: tribeId, expedition_id: command.expeditionId }, header },
            body: { workstream: command.workstream },
          });
  return requiredApiData(
    response.data,
    response.error,
    'Your expedition action could not be confirmed.',
  );
}

export function useTribeExpeditions(tribeId: string, enabled = true) {
  return useQuery({
    queryKey: tribeExpeditionQueryKey(tribeId),
    queryFn: ({ signal }) => loadTribeExpeditions(tribeId, signal),
    staleTime: 0,
    gcTime: 0,
    enabled,
  });
}

export function useExpeditionCommand(tribeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    networkMode: 'always',
    retry: false,
    mutationFn: (command: ExpeditionCommand) => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        throw new Error('Reconnect to join or contribute to your expedition.');
      }
      return submitExpeditionCommand(tribeId, command);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: tribeExpeditionQueryKey(tribeId) }),
        queryClient.invalidateQueries({ queryKey: tribeAssistanceQueryKey(tribeId), exact: true }),
        queryClient.invalidateQueries({ queryKey: tribeSocialQueryKey(tribeId), exact: true }),
        queryClient.invalidateQueries({ queryKey: verticalSliceQueryKey }),
      ]);
    },
  });
}

export interface ExpeditionChallengeCommand {
  expeditionId: string;
  workstream: ExpeditionWorkstream;
  commandId: string;
}

export async function launchExpeditionChallenge(
  tribeId: string,
  command: ExpeditionChallengeCommand,
) {
  if (!navigator.onLine) throw new Error('Reconnect before opening an expedition challenge.');
  const response = await browserApiClient({ mutation: true }).POST(
    '/api/v1/tribes/{tribe_id}/expeditions/{expedition_id}/challenge',
    {
      params: {
        path: { tribe_id: tribeId, expedition_id: command.expeditionId },
        header: { 'Idempotency-Key': command.commandId },
      },
      body: { workstream: command.workstream },
    },
  );
  return requiredApiData(
    response.data,
    response.error,
    'Your expedition challenge could not be opened.',
  );
}

export function useExpeditionChallenge(tribeId: string) {
  return useMutation({
    mutationFn: (command: ExpeditionChallengeCommand) =>
      launchExpeditionChallenge(tribeId, command),
    networkMode: 'always',
    retry: false,
  });
}
